import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { Avatar } from "@/components/Avatar";
import { isToolsOnly } from "@/auth/role";
import { navFor } from "@/shell/nav";
import { useAuth } from "@/auth/AuthContext";
import { api, userAvatarUrl } from "@/api/client";
import { SupportMenu } from "@/pages/Support/SupportMenu";

const ROLE_LABEL: Record<string, string> = {
  recruiter: "Рекрутёр",
  sourcing_lead: "Лид подбора",
  sysadmin: "Администратор",
  tools: "Инструменты",
};

export function Sidebar({
  collapsed = false,
  onToggleCollapse,
  onOpenSearch,
}: {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onOpenSearch?: () => void;
}) {
  const { state, logout } = useAuth();
  const [avatarVer, setAvatarVer] = useState(0);
  // Плашка с названием пункта свёрнутого меню (как _navTip в эталоне):
  // fixed-координаты считаются от пункта, по центру его высоты, 8px вправо.
  const [tip, setTip] = useState<{ text: string; left: number; centerY: number } | null>(null);
  const [tipTop, setTipTop] = useState<number | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  // Высоту плашки знаем только после отрисовки — тогда и ставим top, прижимая
  // к краям окна (до этого она невидима, чтобы не мигнуть в чужом месте).
  useLayoutEffect(() => {
    if (!tip || !tipRef.current) {
      setTipTop(null);
      return;
    }
    const h = tipRef.current.offsetHeight;
    setTipTop(Math.max(8, Math.min(window.innerHeight - h - 8, tip.centerY - h / 2)));
  }, [tip]);
  const showTip = (el: HTMLElement, text: string) => {
    if (!collapsed) return;
    const r = el.getBoundingClientRect();
    setTip({ text, left: r.right + 8, centerY: r.top + r.height / 2 });
  };
  const hideTip = () => setTip(null);
  // Меню развернули или свернули — плашка от прежнего наведения уже не к месту.
  useEffect(() => {
    setTip(null);
  }, [collapsed]);
  const user = state.status === "authed" ? state.user : null;
  const name = user?.full_name ?? "—";
  const role = user ? (ROLE_LABEL[user.role] ?? user.role) : "";
  // Поиск по всему ищет кандидатов и вакансии — роли «Инструменты» он закрыт
  // на сервере, и кнопка, которая ничего не находит, хуже отсутствующей.
  const canSearch = !isToolsOnly(user);

  return (
    <aside className="sidebar">
      <div className="sidebar-head">
        <div className="brand">
          <span className="brand-logo" aria-hidden="true">
            <svg viewBox="0 0 26 26" width="26" height="26">
              <rect width="26" height="26" rx="2" fill="var(--accent)" />
              <rect x="7.5" y="7" width="2.5" height="12" fill="#fff" />
              <rect x="16" y="7" width="2.5" height="12" fill="#fff" />
              <rect x="7.5" y="11.75" width="11" height="2.5" fill="#fff" />
            </svg>
          </span>
          <span className="brand-name">HRDeva</span>
        </div>
        <div className="brand-actions">
          {canSearch && (
            <button
              className="icon-btn brand-search"
              type="button"
              aria-label="Поиск по всему"
              title="Поиск по всему (Ctrl+K)"
              onClick={onOpenSearch}
            >
              <Icon name="search" size={17} />
            </button>
          )}
          <button
            className="icon-btn"
            type="button"
            aria-label="Свернуть меню"
            aria-expanded={!collapsed}
            title="Свернуть меню"
            onClick={onToggleCollapse}
          >
            <Icon name="sidebar" size={17} />
          </button>
        </div>
      </div>

      <nav className="sidebar-nav">
        {navFor(user).map((section) => (
          <div className="nav-section" key={section.title || "main"}>
            {section.title && (
              <div className="nav-section-title">{section.title}</div>
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                // В свёрнутом виде подписи скрыты, остаются одни значки —
                // название пункта тогда в плашке справа (.nav-tip), не в title.
                aria-label={collapsed ? item.label : undefined}
                onMouseEnter={(e) => showTip(e.currentTarget, item.label)}
                onMouseLeave={hideTip}
                onFocus={(e) => showTip(e.currentTarget, item.label)}
                onBlur={hideTip}
                onClick={hideTip}
                className={({ isActive }) =>
                  "nav-item" + (isActive ? " nav-item--active" : "")
                }
              >
                <Icon name={item.icon} size={17} className="nav-item-icon" />
                <span className="nav-item-label">{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* «Администратор» (только sysadmin) и «Поддержка» — прижаты вниз, над блоком пользователя; роли «Инструменты» они закрыты на сервере. */}
      {canSearch && <SupportMenu collapsed={collapsed} showTip={showTip} hideTip={hideTip} />}

      <div className="sidebar-foot">
        <div className="account">
          <label className="account-avatar-pick" title="Сменить аватар">
            <Avatar
              name={name}
              src={user ? `${userAvatarUrl(user.id)}?v=${avatarVer}` : null}
              size={32}
            />
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              hidden
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                await api.uploadMyAvatar(f).catch(() => {});
                setAvatarVer((v) => v + 1);
              }}
            />
          </label>
          <span className="account-meta">
            <span className="account-name">{name}</span>
            <span className="account-role">{role}</span>
          </span>
          <button
            className="icon-btn account-logout"
            type="button"
            aria-label="Выйти"
            title="Выйти"
            onClick={() => logout()}
          >
            <Icon name="logout" size={16} />
          </button>
        </div>
      </div>

      {collapsed && tip && (
        <div
          ref={tipRef}
          className="nav-tip"
          role="tooltip"
          style={{ left: tip.left, top: tipTop ?? tip.centerY, visibility: tipTop === null ? "hidden" : "visible" }}
        >
          {tip.text}
        </div>
      )}
    </aside>
  );
}
